import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'
import { Button, Text, TextInput } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { type FeePaidBy, useAdminSettings, useSaveAdminSettings } from '@hooks'
import { commissionBpsToInput, formatAdminDateTime, formatBps, parseCommissionPercent } from '@utils'
import type { AdminStackParamList } from '../../../navigation/navigator/AdminStackNavigator'
import { OptionChips } from '../events/components/OptionChips'
import { AdminPage } from './components/AdminPage'

type Props = NativeStackScreenProps<AdminStackParamList, 'AdminSettings'>

/** Platform settings: the default commission and who bears the fee (future payments only). */
export const AdminSettings: React.FC<Props> = ({ navigation }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyles(theme)
  const settings = useAdminSettings()
  const save = useSaveAdminSettings()
  const [rate, setRate] = useState('')
  const [feePaidBy, setFeePaidBy] = useState<FeePaidBy>('attendee')

  // Fill the form once from the server; a later refetch must not overwrite what is being typed.
  const filled = useRef(false)
  useEffect(() => {
    if (settings.data && settings.isFetchedAfterMount && !filled.current) {
      filled.current = true
      setRate(commissionBpsToInput(settings.data.commissionRateBps))
      setFeePaidBy(settings.data.feePaidBy)
    }
  }, [settings.data, settings.isFetchedAfterMount])

  const bps = parseCommissionPercent(rate)
  const rateInvalid = rate.trim() !== '' && bps === null
  const canSave = bps !== null && !save.isPending && !!settings.data
  const feeLabel = (value: FeePaidBy) =>
    t(value === 'attendee' ? 'admin.organizer.feeAttendee' : 'admin.organizer.feeOrganizer')
  const rateLabel = (value: number) =>
    value === 0 ? t('admin.organizer.noCommission') : `${formatBps(value, currentLanguage)} %`

  return (
    <AdminPage
      title={t('admin.settings.title')}
      onBack={() => (navigation.getParent() as any)?.navigate('MenuMain')}
      active="settings"
    >
      {settings.isLoading ? (
        <ActivityIndicator color={theme.colors.primary} />
      ) : settings.isError || !settings.data ? (
        <Text type="bodySmall" color={theme.colors.error} testID="admin-settings-error">
          {t('admin.settings.error')}
        </Text>
      ) : (
        <>
          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.settings.commissionTitle')}</Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('admin.settings.commissionHint')}
            </Text>
            {settings.data.isDefault ? (
              <Text type="bodySmall" color={theme.colors.orange} testID="admin-settings-default-note">
                {t('admin.settings.serverValue')}
              </Text>
            ) : null}
            <TextInput
              testID="admin-settings-rate"
              title={t('admin.organizer.commissionLabel')}
              value={rate}
              onChangeText={setRate}
              keyboardType="decimal-pad"
              hasError={rateInvalid}
              errorMessage={rateInvalid ? t('admin.organizer.commissionInvalid') : undefined}
            />
            <Text
              type="bodySmallBold"
              color={bps === 0 ? theme.colors.green : theme.colors.secondary}
              testID="admin-settings-summary"
            >
              {bps === null
                ? ' '
                : bps === 0
                  ? t('admin.organizer.noCommission')
                  : t('admin.organizer.commissionSummary', { rate: commissionBpsToInput(bps) })}
            </Text>
            <OptionChips<FeePaidBy>
              testID="admin-settings-fee"
              label={t('admin.organizer.feePaidBy')}
              options={[
                { value: 'attendee', label: t('admin.organizer.feeAttendee') },
                { value: 'organizer', label: t('admin.organizer.feeOrganizer') },
              ]}
              value={feePaidBy}
              onChange={(value) => value && setFeePaidBy(value)}
            />
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {t('admin.organizer.appliesToFuture')}
            </Text>
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {t('admin.settings.ownSettingWins')}
            </Text>
            <Button
              testID="admin-settings-save"
              title={t('admin.organizer.save')}
              disabled={!canSave}
              onPress={() => {
                if (bps !== null) save.mutate({ commissionRateBps: bps, feePaidBy })
              }}
            />
          </View>

          <View style={styles.card}>
            <Text type="subtitleBold">{t('admin.settings.historyTitle')}</Text>
            {settings.data.history.length === 0 ? (
              <Text type="bodySmall" color={theme.colors.greyBlue} testID="admin-settings-history-empty">
                {t('admin.settings.historyEmpty')}
              </Text>
            ) : (
              settings.data.history.map((change, index) => (
                <View
                  key={`${change.changedAt}-${index}`}
                  style={styles.change}
                  testID={`admin-settings-change-${index}`}
                >
                  <Text type="bodySmallBold">
                    {`${rateLabel(change.previous.commissionRateBps)} (${feeLabel(change.previous.feePaidBy)}) → ${rateLabel(change.next.commissionRateBps)} (${feeLabel(change.next.feePaidBy)})`}
                  </Text>
                  <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
                    {t('admin.settings.changedBy', {
                      name: change.changedBy?.name || change.changedBy?.email || t('admin.settings.unknownAdmin'),
                      date: formatAdminDateTime(change.changedAt, currentLanguage),
                    })}
                  </Text>
                </View>
              ))
            )}
          </View>
        </>
      )}
    </AdminPage>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      gap: theme.spacing.md,
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
    change: {
      gap: 2,
      paddingTop: theme.spacing.sm,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey,
    },
  })
